/**
 * @file        src/components/SeoHead.jsx
 * @description The ONE global head manager, mounted once in App() beside
 *              <FaviconManager />. Keyed on the router path, it asks the SEO API
 *              for this path's tags and writes them with src/lib/seoHead.js —
 *              so every route, the noindex funnel pages included, is covered
 *              without a per-page <Seo> component. Renders nothing.
 *
 *              While the record is loading, or when the API cannot be reached,
 *              it applies ONLY fallbackRobots(path): a search or private route
 *              (and any path no route serves) is never left on index.html's
 *              "index, follow" default, so an API outage can never expose a
 *              voucher or an invoice to a crawler. Everything else keeps the
 *              static defaults.
 *
 *              Analytics load once per page life: GA4 from the record, falling
 *              back to VITE_GA_MEASUREMENT_ID when the API supplies none or
 *              fails (set only on the Neon build — Beyond has no fallback and
 *              must never report into Neon's property); GTM and the Meta Pixel
 *              only when the record names them.
 *
 *              BYTE-IDENTICAL in the neon and beyond builds.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSeo } from '../api/seo.js';
import { fallbackRobots, normalizePath } from '../lib/seoRoutes.js';
import { applySeo, setTag, loadGa, loadGtm, loadFacebookPixel } from '../lib/seoHead.js';

const SEO_STALE_MS = 5 * 60 * 1000;
const GA_FALLBACK = import.meta.env.VITE_GA_MEASUREMENT_ID || null;

export default function SeoHead() {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);

  const { data, isSuccess, isError } = useQuery({
    queryKey: ['seo', path],
    queryFn: () => fetchSeo(path),
    staleTime: SEO_STALE_MS,
    retry: 1,
  });

  // The title the API resolved for THIS path, or null while we have none.
  // Pages still set document.title on mount (and a lazy chunk can mount after
  // the record was applied), so a resolved title is re-asserted when anything
  // else overwrites it. With no record (loading / API down) the page's own
  // title stands.
  const wantedTitle = useRef(null);

  useEffect(() => {
    const el = document.head.querySelector('title');
    if (!el || typeof MutationObserver === 'undefined') return undefined;
    const obs = new MutationObserver(() => {
      const want = wantedTitle.current;
      if (want && document.title !== want) document.title = want;
    });
    obs.observe(el, { childList: true, characterData: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (data) {
      wantedTitle.current = data.title || null;
      applySeo(data);
      return;
    }
    wantedTitle.current = null;
    const robots = fallbackRobots(path);
    if (robots) setTag('robots', robots);
  }, [data, path]);

  // Analytics: decided once the first record settles, either way.
  useEffect(() => {
    if (!isSuccess && !isError) return;
    loadGa(data?.analytics?.ga || GA_FALLBACK);
    if (data?.analytics?.gtm) loadGtm(data.analytics.gtm);
    if (data?.analytics?.facebookPixel) loadFacebookPixel(data.analytics.facebookPixel);
  }, [isSuccess, isError, data]);

  return null;
}
